/**
 * UC-24: TriggerScriptedEvent
 * TOGAF Application ABB — scenario narrative events at tick boundaries without UI deps.
 */
import type { RandomEvent, Scenario } from '../../../types';
import type { IAppLogger } from '../../domain/ports/IAppLogger';

export interface TriggerScriptedEventInput {
  nextTick: number;
  activeScenario: Scenario | null;
  eventActive: boolean;
}

export interface ScriptedStatAdjustments {
  compliance?: number;
  budget?: number;
  threat?: number;
}

export interface TriggerScriptedEventOutput {
  triggered: boolean;
  event?: RandomEvent;
  statAdjustments?: ScriptedStatAdjustments;
  playAlertSfx?: boolean;
}

const SCRIPTED_STAT_ADJUSTMENTS: Record<string, ScriptedStatAdjustments> = {
  reg_inquiry: { compliance: -10 },
  comp_decay_alert: { compliance: -8 },
  ft_report: { compliance: -20 },
  audit_fail: { compliance: -40 },
  ceo_arrest: { compliance: -30 },
  liquidity_crunch: { budget: -2000 },
  sec_inquiry: { compliance: -15 },
  customer_run: { budget: -1000 },
  zero_day: { threat: 30 },
  data_exfil: { threat: 20 },
  regulatory_deadline: { compliance: -20 },
};

export interface TriggerScriptedEventDependencies {
  logger?: IAppLogger;
}

export class TriggerScriptedEventUseCase {
  constructor(private readonly deps: TriggerScriptedEventDependencies = {}) {}

  execute(input: TriggerScriptedEventInput): TriggerScriptedEventOutput {
    const { nextTick, activeScenario, eventActive } = input;

    if (eventActive || !activeScenario?.scriptedEvents?.length) {
      return { triggered: false };
    }

    const scripted = activeScenario.scriptedEvents.find((e) => e.triggerTick === nextTick);
    if (!scripted) {
      return { triggered: false };
    }

    const statAdjustments = SCRIPTED_STAT_ADJUSTMENTS[scripted.event.id];

    this.deps.logger?.log('debug', 'scripted_event.triggered', {
      scenarioId: activeScenario.id,
      tick: nextTick,
      eventId: scripted.event.id,
    });

    return {
      triggered: true,
      event: scripted.event,
      statAdjustments,
      playAlertSfx: true,
    };
  }
}